import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Landmark, CreditCard, Wallet, TrendingDown, Search, Building } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-bold">{value}</p></div>
      </CardContent>
    </Card>
  );
}

export default function BankingCredit() {
  const [accounts, setAccounts] = useState([]);
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("accounts");
  const [search, setSearch] = useState("");

  const [acctOpen, setAcctOpen] = useState(false);
  const [acctEdit, setAcctEdit] = useState(null);
  const emptyAcct = { name: "", bank: "", account_type: "checking", last4: "", currency: "USD", balance: 0, is_primary: false, notes: "" };
  const [acctForm, setAcctForm] = useState(emptyAcct);

  const [clOpen, setClOpen] = useState(false);
  const [clEdit, setClEdit] = useState(null);
  const emptyCl = { name: "", lender: "", credit_type: "line_of_credit", credit_limit: 0, current_balance: 0, interest_rate: 0, minimum_payment: "", next_payment_due: "", notes: "" };
  const [clForm, setClForm] = useState(emptyCl);

  const load = async () => {
    try {
      const [aRes, cRes, sRes] = await Promise.all([
        axios.get(`${API}/banking/accounts`),
        axios.get(`${API}/banking/credit-lines`),
        axios.get(`${API}/banking/stats`),
      ]);
      setAccounts(aRes.data || []); setCredits(cRes.data || []); setStats(sRes.data || {});
    } catch { toast.error("Failed to load banking data"); }
  };
  useEffect(() => { load(); }, []);

  const saveAcct = async () => {
    if (!acctForm.name.trim()) { toast.error("Account name required"); return; }
    const payload = { ...acctForm, balance: Number(acctForm.balance) || 0 };
    try {
      if (acctEdit) await axios.put(`${API}/banking/accounts/${acctEdit.id}`, payload);
      else await axios.post(`${API}/banking/accounts`, payload);
      toast.success(acctEdit ? "Account updated" : "Account added"); setAcctOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delAcct = async (id) => { if (!window.confirm("Delete this account?")) return;
    try { await axios.delete(`${API}/banking/accounts/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const saveCl = async () => {
    if (!clForm.name.trim()) { toast.error("Credit line name required"); return; }
    const payload = { ...clForm,
      credit_limit: Number(clForm.credit_limit) || 0,
      current_balance: Number(clForm.current_balance) || 0,
      interest_rate: Number(clForm.interest_rate) || 0,
      minimum_payment: clForm.minimum_payment === "" ? null : Number(clForm.minimum_payment),
    };
    if (!payload.next_payment_due) delete payload.next_payment_due;
    if (payload.minimum_payment === null) delete payload.minimum_payment;
    try {
      if (clEdit) await axios.put(`${API}/banking/credit-lines/${clEdit.id}`, payload);
      else await axios.post(`${API}/banking/credit-lines`, payload);
      toast.success(clEdit ? "Credit line updated" : "Credit line added"); setClOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delCl = async (id) => { if (!window.confirm("Delete this credit line?")) return;
    try { await axios.delete(`${API}/banking/credit-lines/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const filteredAccts = accounts.filter((a) => !search || `${a.name} ${a.bank || ""} ${a.account_type}`.toLowerCase().includes(search.toLowerCase()));
  const filteredCls = credits.filter((c) => !search || `${c.name} ${c.lender || ""} ${c.credit_type}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="banking-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Banking &amp; Credit</h1>
        <p className="text-sm text-muted-foreground mt-1">Track business bank accounts, credit lines, and cash flow at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Wallet} label="Total Cash" value={`$${(stats.total_cash ?? 0).toLocaleString()}`} tint="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={Landmark} label="Accounts" value={stats.total_accounts ?? 0} tint="bg-primary/10 text-primary" />
        <StatCard icon={CreditCard} label="Credit Limit" value={`$${(stats.total_credit_limit ?? 0).toLocaleString()}`} tint="bg-blue-500/10 text-blue-600" />
        <StatCard icon={TrendingDown} label="Credit Used" value={`$${(stats.total_credit_used ?? 0).toLocaleString()}`} tint="bg-amber-500/10 text-amber-600" />
        <StatCard icon={Building} label="Utilization" value={`${stats.credit_utilization ?? 0}%`} tint="bg-red-500/10 text-red-600" />
      </div>

      {(stats.total_credit_limit ?? 0) > 0 && (
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">Credit Utilization</p>
            <p className="text-sm text-muted-foreground">${(stats.total_credit_used ?? 0).toLocaleString()} / ${(stats.total_credit_limit ?? 0).toLocaleString()} · <span className={stats.credit_utilization > 30 ? "text-amber-600" : "text-emerald-600"}>${(stats.available_credit ?? 0).toLocaleString()} available</span></p>
          </div>
          <Progress value={stats.credit_utilization ?? 0} className="h-3" />
        </CardContent></Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex justify-between items-center mb-2">
          <TabsList>
            <TabsTrigger value="accounts" data-testid="tab-accounts">Bank Accounts</TabsTrigger>
            <TabsTrigger value="credit" data-testid="tab-credit">Credit Lines</TabsTrigger>
          </TabsList>
          <div className="flex gap-2 items-center">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" /></div>
            {tab === "accounts"
              ? <Button onClick={() => { setAcctEdit(null); setAcctForm(emptyAcct); setAcctOpen(true); }} data-testid="add-account-btn"><Plus className="h-4 w-4 mr-2" />Add Account</Button>
              : <Button onClick={() => { setClEdit(null); setClForm(emptyCl); setClOpen(true); }} data-testid="add-credit-line-btn"><Plus className="h-4 w-4 mr-2" />Add Credit Line</Button>}
          </div>
        </div>

        <TabsContent value="accounts">
          <Card><CardContent className="p-4">
            {filteredAccts.length === 0 ? <div className="py-12 text-center text-muted-foreground"><Landmark className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No bank accounts tracked yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Bank</TableHead><TableHead>Type</TableHead><TableHead>Last 4</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Primary</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredAccts.map((a) => (
                <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-sm">{a.bank || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{a.account_type}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{a.last4 ? `••${a.last4}` : "—"}</TableCell>
                  <TableCell className="text-right font-medium">{a.currency || "USD"} ${Number(a.balance || 0).toLocaleString()}</TableCell>
                  <TableCell>{a.is_primary ? <Badge className="bg-primary/10 text-primary border-primary/30">Primary</Badge> : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setAcctEdit(a); setAcctForm({ ...emptyAcct, ...a }); setAcctOpen(true); }} data-testid={`edit-account-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delAcct(a.id)} data-testid={`delete-account-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="credit">
          <Card><CardContent className="p-4">
            {filteredCls.length === 0 ? <div className="py-12 text-center text-muted-foreground"><CreditCard className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No credit lines tracked yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Lender</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Limit</TableHead><TableHead className="text-right">Used</TableHead><TableHead>Rate</TableHead><TableHead>Next Due</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredCls.map((c) => {
                const util = c.credit_limit > 0 ? Math.round((c.current_balance / c.credit_limit) * 100) : 0;
                return (
                <TableRow key={c.id} data-testid={`credit-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm">{c.lender || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{c.credit_type?.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-right">${Number(c.credit_limit || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right"><div className="flex items-center justify-end gap-2"><span>${Number(c.current_balance || 0).toLocaleString()}</span><Badge variant="outline" className={util > 30 ? "bg-amber-500/10 text-amber-600 border-amber-500/30" : "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"}>{util}%</Badge></div></TableCell>
                  <TableCell>{c.interest_rate}%</TableCell>
                  <TableCell className="text-sm">{c.next_payment_due ? new Date(c.next_payment_due).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setClEdit(c); setClForm({ ...emptyCl, ...c, minimum_payment: c.minimum_payment ?? "" }); setClOpen(true); }} data-testid={`edit-credit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delCl(c.id)} data-testid={`delete-credit-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );})}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Account Dialog */}
      <Dialog open={acctOpen} onOpenChange={setAcctOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{acctEdit ? "Edit Account" : "Add Bank Account"}</DialogTitle><DialogDescription>Add a bank account to track your business cash position.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Account Name *</Label><Input value={acctForm.name} onChange={(e) => setAcctForm({ ...acctForm, name: e.target.value })} data-testid="account-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank</Label><Input value={acctForm.bank || ""} onChange={(e) => setAcctForm({ ...acctForm, bank: e.target.value })} /></div>
              <div><Label>Type</Label>
                <Select value={acctForm.account_type} onValueChange={(v) => setAcctForm({ ...acctForm, account_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="checking">Checking</SelectItem><SelectItem value="savings">Savings</SelectItem><SelectItem value="money_market">Money Market</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Last 4</Label><Input maxLength={4} value={acctForm.last4 || ""} onChange={(e) => setAcctForm({ ...acctForm, last4: e.target.value })} /></div>
              <div><Label>Currency</Label><Input value={acctForm.currency || "USD"} onChange={(e) => setAcctForm({ ...acctForm, currency: e.target.value.toUpperCase() })} /></div>
              <div><Label>Balance</Label><Input type="number" step="0.01" value={acctForm.balance} onChange={(e) => setAcctForm({ ...acctForm, balance: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={acctForm.is_primary} onCheckedChange={(v) => setAcctForm({ ...acctForm, is_primary: v })} /><Label className="m-0">Primary Account</Label></div>
            <div><Label>Notes</Label><Textarea rows={2} value={acctForm.notes || ""} onChange={(e) => setAcctForm({ ...acctForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAcctOpen(false)}>Cancel</Button><Button onClick={saveAcct} data-testid="save-account-btn">{acctEdit ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Line Dialog */}
      <Dialog open={clOpen} onOpenChange={setClOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{clEdit ? "Edit Credit Line" : "Add Credit Line"}</DialogTitle><DialogDescription>Track credit limits, balances, and upcoming payments.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={clForm.name} onChange={(e) => setClForm({ ...clForm, name: e.target.value })} data-testid="credit-name-input" /></div>
              <div><Label>Type</Label>
                <Select value={clForm.credit_type} onValueChange={(v) => setClForm({ ...clForm, credit_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="line_of_credit">Line of Credit</SelectItem><SelectItem value="credit_card">Credit Card</SelectItem><SelectItem value="loan">Loan</SelectItem><SelectItem value="mortgage">Mortgage</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Lender</Label><Input value={clForm.lender || ""} onChange={(e) => setClForm({ ...clForm, lender: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Credit Limit ($)</Label><Input type="number" step="0.01" value={clForm.credit_limit} onChange={(e) => setClForm({ ...clForm, credit_limit: e.target.value })} /></div>
              <div><Label>Current Balance ($)</Label><Input type="number" step="0.01" value={clForm.current_balance} onChange={(e) => setClForm({ ...clForm, current_balance: e.target.value })} /></div>
              <div><Label>Rate (%)</Label><Input type="number" step="0.01" value={clForm.interest_rate} onChange={(e) => setClForm({ ...clForm, interest_rate: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min. Payment ($)</Label><Input type="number" step="0.01" value={clForm.minimum_payment} onChange={(e) => setClForm({ ...clForm, minimum_payment: e.target.value })} /></div>
              <div><Label>Next Payment Due</Label><Input type="date" value={clForm.next_payment_due || ""} onChange={(e) => setClForm({ ...clForm, next_payment_due: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={clForm.notes || ""} onChange={(e) => setClForm({ ...clForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setClOpen(false)}>Cancel</Button><Button onClick={saveCl} data-testid="save-credit-btn">{clEdit ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
